import { lazy, ComponentType } from "react";

/**
 * Wraps React.lazy() with automatic retry and page reload on failure.
 * Solves the "Failed to fetch dynamically imported module" error
 * caused by stale browser/PWA cache after deployments.
 *
 * Strategy:
 * 1. First attempt: normal import
 * 2. On failure: retry once with cache-busted URL
 * 3. If still failing: force reload the page (once per session per module)
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  moduleId?: string
) {
  return lazy(() =>
    factory().catch((error) => {
      console.warn(`[lazyWithRetry] First attempt failed for ${moduleId || 'unknown'}:`, error?.message);

      // Retry once — the browser may fetch a fresh version
      return factory().catch((retryError) => {
        console.error(`[lazyWithRetry] Retry failed for ${moduleId || 'unknown'}:`, retryError?.message);

        // Prevent infinite reload loops: only reload once per module per session
        const reloadKey = `lazy_reload_${moduleId || 'global'}`;
        const alreadyReloaded = sessionStorage.getItem(reloadKey);

        if (!alreadyReloaded) {
          sessionStorage.setItem(reloadKey, Date.now().toString());
          console.log(`[lazyWithRetry] Forcing page reload for ${moduleId || 'unknown'}`);
          window.location.reload();
        }

        // If we already reloaded once and it still fails, throw to show error boundary
        throw retryError;
      });
    })
  );
}
